import { FormEvent, useState } from 'react'
import styles from './PromoCode.module.scss'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { Button, TextField } from '@boltenergy-be/design-system'
import { CodeTypes } from 'types/promo-referral-codes'
import { PromoCodeProps } from 'components/promo-code/types'
import { InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { requestAndSavePropositions } from 'features/simulation/utils'
import { updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { Language } from 'types/language'
import axios from 'axios'
import { setPromoOrReferralCodeInStore } from 'utils/customerFlow'
import { CheckPromoReferralCodeResponse, NextApiResponseBody } from 'types/boltApi/request'
import { PLENTY_ONLINE_PROMO_CODE } from 'constants/promo-codes'

const PromoCode = ({ withDividers, className }: PromoCodeProps) => {
  // Redux store
  const { simulation } = useStoreSelector((store) => store.customer.inputs)
  const { confirmationFromPortalRegistration } = useStoreSelector((store) => store.marketing)
  const { promoCode, referralCode } = simulation
  const promoReferralCode = referralCode || promoCode
  const dispatch = useStoreDispatch()

  // Local state
  const [code, setCode] = useState<string>()
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isValid, setIsValid] = useState<boolean>(false)
  const [showCodeInputField, setShowCodeInputField] = useState<boolean>(false)

  // I18n
  const { t } = useTranslation('producer')

  // Router
  const { locale } = useRouter()

  /**
   * Handles the form submit
   * Checks if the promo/referral code is valid and saves it in the store if so
   *
   * @param {FormEvent} e
   */
  const handleOnSubmit = async (e: FormEvent) => {
    e.preventDefault()

    // Update loading
    setIsLoading(true)

    // Check if promo code is valid
    const {
      data: { data: checkedPromoReferralCode }
    } = await axios.get<NextApiResponseBody<CheckPromoReferralCodeResponse>>(`/api/website/promo-referral-code?code=${code}`)

    // Set isValid
    setIsValid(checkedPromoReferralCode.valid)

    // Save & resimulate if valid
    if (checkedPromoReferralCode.valid) {
      // Hide code input field
      setShowCodeInputField(false)

      // PROMO CODE
      if (checkedPromoReferralCode.type === CodeTypes.PROMO && !!checkedPromoReferralCode.promoCode) {
        // Save promo code in store
        dispatch(
          updateInput({
            group: InputGroupKeys.SIMULATION,
            key: SimulationFieldKeys.PROMO_CODE,
            value: checkedPromoReferralCode.promoCode
          })
        )
      }

      // REFERRAL CODE
      else if (checkedPromoReferralCode.type === CodeTypes.REFERRAL && !!checkedPromoReferralCode.referralCode) {
        // Save referral code in store
        dispatch(
          updateInput({
            group: InputGroupKeys.SIMULATION,
            key: SimulationFieldKeys.REFERRAL_CODE,
            value: checkedPromoReferralCode.referralCode
          })
        )

        // Fetch & save VIAVIA promo code in store
        await setPromoOrReferralCodeInStore('VIAVIA')
      }

      // Refetch the propositions
      await requestAndSavePropositions()
    }

    if (!isChecked) setIsChecked(true)
    setIsLoading(false)
  }

  return !confirmationFromPortalRegistration ? (
    <>
      {!showCodeInputField && !promoReferralCode && (
        <>
          {withDividers && <hr className={styles.hr} />}
          <div className={classNames(styles['promo-line'], className)}>
            <p>{parse(t('doYouHavePromoCode', 'Heb je een promocode?'))}</p>
            <Button representation="link" onClick={() => setShowCodeInputField(true)}>
              {t('addPromoCode', 'Voeg toe')}
            </Button>
          </div>
        </>
      )}

      {!showCodeInputField && promoReferralCode && (
        <>
          {withDividers && <hr className={styles.hr} />}
          <div className={styles['promo-success']}>
            {'referrer' in promoReferralCode ? (
              <>
                <p>
                  {t('yourReferralCode', 'Je referral code')}
                  {locale === Language.FRENCH && parse('&nbsp;')}:&nbsp;
                  <strong>
                    {promoReferralCode.code}&nbsp;({promoReferralCode.referrer.firstName})
                  </strong>
                </p>
              </>
            ) : (
              promoCode?.code !== PLENTY_ONLINE_PROMO_CODE && (
                <p>
                  {t('yourPromoCode', 'Je promocode')}:&nbsp;
                  <strong>{promoReferralCode.code}</strong>
                </p>
              )
            )}
            {'promoCodeId' in promoReferralCode && 'translations' in promoReferralCode && (
              <p>{parse(promoReferralCode.translations[locale])}</p>
            )}
          </div>
        </>
      )}

      {showCodeInputField && (
        <>
          {withDividers && <hr className={styles.hr} />}
          <form className={styles['promo-code-form']} onSubmit={(e) => handleOnSubmit(e)}>
            <TextField
              label=""
              type="text"
              required
              value={code}
              onChange={(e) => {
                setCode(e.currentTarget.value)
                if (e.currentTarget.value === '') setIsValid(true)
              }}
              suffix={{ as: <Button size="small" loading={isLoading} trailingIcon="plus" type="submit" /> }}
            />
          </form>

          {isChecked && !isValid && <p className="is-red mt-none">{t('invalidPromoCode', 'Sorry, dit is geen geldige code.')}</p>}
        </>
      )}
    </>
  ) : null
}

export default PromoCode
